<template>
  <v-card class="field-intervention" :class="{ 'field-intervention--open': intervention }">
    <v-card-text class="pa-4">
      <div class="d-flex align-center">
        <div class="field-intervention__title">{{ $t('onboarding.field.intervention.title') }}</div>
        <v-spacer />
        <v-chip v-if="intervention" x-small dark color="primary">{{ $t(`onboarding.field.intervention.kind.${intervention.kind}`) }}</v-chip>
      </div>

      <!-- Pas encore commencée : on la commence en arrivant sur la borne. -->
      <template v-if="!intervention">
        <p class="text-body-2 text--secondary">{{ $t('onboarding.field.intervention.hint') }}</p>
        <v-btn small depressed color="primary" :loading="working" :disabled="!loaded" @click="open">
          <v-icon left small>mdi-account-hard-hat</v-icon>{{ $t('onboarding.field.intervention.start') }}
        </v-btn>
      </template>

      <!-- Commencée : depuis quand, le mode test et son compte à rebours, la fin. -->
      <template v-else>
        <p class="text-body-2 mb-3">{{ $t('onboarding.field.intervention.since', { time: $options.filters.formatDate(intervention.openedAt, 'HH:mm') }) }}</p>
        <div class="field-intervention__test" :class="{ 'field-intervention__test--active': intervention.testMode }">
          <v-icon :color="intervention.testMode ? 'success' : 'grey'" class="mr-2">{{ intervention.testMode ? 'mdi-flask' : 'mdi-flask-outline' }}</v-icon>
          <div class="flex-grow-1 text-body-2">
            <div class="font-weight-medium">{{ $t(intervention.testMode ? 'onboarding.field.intervention.test-mode-on' : 'onboarding.field.intervention.test-mode-off') }}</div>
            <div v-if="intervention.testMode" class="text--secondary">{{ $t('onboarding.field.intervention.test-mode-left', { time: countdown }) }}</div>
            <div v-else class="text--secondary">{{ $t('onboarding.field.intervention.test-mode-hint') }}</div>
          </div>
          <v-btn small :outlined="intervention.testMode" depressed color="primary" :loading="working" @click="change('activate-test-mode')">
            {{ $t(intervention.testMode ? 'onboarding.field.intervention.test-mode-again' : 'onboarding.field.intervention.test-mode-start') }}
          </v-btn>
        </div>

        <!-- En mode test : le membre simulé, et le badge de test qui le porte. -->
        <div v-if="intervention.testMode && canManage" class="field-intervention__simulation">
          <div class="text-body-2 font-weight-medium mb-1">{{ $t('onboarding.field.intervention.simulation.title') }}</div>
          <p class="text-caption text--secondary mb-2">{{ $t('onboarding.field.intervention.simulation.hint', { minutes: intervention.testChargeCap.minutes, energy: intervention.testChargeCap.energyWh / 1000 }) }}</p>
          <v-autocomplete
              v-model="memberId"
              :items="members"
              :search-input.sync="memberSearch"
              :loading="searching"
              :disabled="working"
              item-value="id"
              item-text="name"
              no-filter
              hide-no-data
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-account-search"
              :label="$t('onboarding.field.intervention.simulation.member')"
              @change="simulate"
              @focus="members.length <= 1 && searchMembers(memberSearch)"
          >
            <template #item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.organization">{{ item.organization }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <div v-if="intervention.simulation" class="text-body-2 mt-2">
            <v-icon small class="mr-1">mdi-card-account-details-outline</v-icon>
            <span v-if="intervention.simulation.badgeUid">{{ $t('onboarding.field.intervention.simulation.badge', { badge: intervention.simulation.badgeUid }) }}</span>
            <span v-else class="text--secondary">{{ $t('onboarding.field.intervention.simulation.no-badge') }}</span>
          </div>
        </div>

        <div class="d-flex mt-3">
          <v-spacer />
          <v-btn small text color="primary" :disabled="working" @click="change('close')">{{ $t('onboarding.field.intervention.close') }}</v-btn>
        </div>
      </template>

      <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">{{ error }}</v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'

/**
 * L'intervention du technicien sur cette borne : il la commence en arrivant (sa pose, depuis la commande, ou un
 * dépannage), y active le mode test pour une heure (relançable), et la termine en partant. Oubliée, elle se ferme seule
 * au bout de 12 heures. En mode test, il choisit le membre à simuler ; le test de badge désigne le badge qui le porte
 * (`designateBadge`), la charge d'essai se lance à son nom.
 */
export default {
  name: 'FieldIntervention',

  mixins: [CommissioningMixin],

  props: {
    chargingPointId: { type: String, required: true },
    // Venue de la commande : l'intervention est sa pose.
    orderId: { type: String, default: null },
    // Juste posée : l'intervention de pose commence d'elle-même.
    autoOpen: { type: Boolean, default: false },
    // Simuler un membre fait charger gratuitement : réservé au droit de piloter une borne.
    canManage: { type: Boolean, default: false },
  },

  data() {
    return {
      intervention: null,
      autoOpened: false,
      loaded: false,
      working: false,
      error: null,
      now: Date.now(),
      clock: null,
      memberId: null,
      members: [],
      memberSearch: null,
      searching: false,
      searchTimer: null,
    }
  },

  computed: {
    selectedMemberName() {
      return this.members.find(m => m.id === this.memberId)?.name || null
    },

    countdown() {
      const left = Math.max(0, Math.floor((new Date(this.intervention?.testModeUntil || 0).getTime() - this.now) / 1000))
      return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`
    },
  },

  watch: {
    chargingPointId() {
      this.load()
    },

    memberSearch(text) {
      clearTimeout(this.searchTimer)
      if (!this.intervention?.testMode || (text && text === this.selectedMemberName)) {
        return
      }
      this.searchTimer = setTimeout(() => this.searchMembers(text), 300)
    },

    // Le membre simulé tel que le serveur le garde : affiché dans le champ.
    'intervention.simulation': {
      immediate: true,
      handler(simulation) {
        this.memberId = simulation?.memberId || null
        if (simulation && !this.members.some(m => m.id === simulation.memberId)) {
          this.members = [{ id: simulation.memberId, name: simulation.memberName, organization: null }, ...this.members]
        }
      },
    },
  },

  mounted() {
    this.load()
    // Le compte à rebours ; à l'échéance, on relit l'état réel.
    this.clock = setInterval(() => {
      this.now = Date.now()
      if (this.intervention?.testMode && new Date(this.intervention.testModeUntil).getTime() <= this.now) {
        this.load()
      }
    }, 1000)
  },

  beforeDestroy() {
    clearInterval(this.clock)
    clearTimeout(this.searchTimer)
  },

  methods: {
    async load() {
      try {
        this.intervention = (await FieldRepository.intervention(this.chargingPointId)).intervention
        this.$emit('change', this.intervention)
        if (!this.intervention && this.autoOpen && !this.autoOpened) {
          this.autoOpened = true
          await this.open()
        }
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.loaded = true
      }
    },

    async open() {
      await this.run(async () => {
        this.intervention = await FieldRepository.openIntervention(this.chargingPointId, this.orderId)
      })
    },

    async change(change) {
      await this.run(async () => {
        const intervention = await FieldRepository.changeIntervention(this.intervention.id, change)
        this.intervention = intervention.open ? intervention : null
      })
    },

    async searchMembers(text) {
      this.searching = true
      try {
        this.members = (await FieldRepository.testMembers(this.intervention.id, text || '')).items
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.searching = false
      }
    },

    // Changer de membre garde le badge de test déjà désigné.
    async simulate(memberId) {
      if (!memberId) {
        return
      }
      await this.run(async () => {
        this.intervention = await FieldRepository.simulateMember(this.intervention.id, memberId, this.intervention.simulation?.badgeUid || null)
      })
    },

    /** Le badge passé au test de badge devient celui du test : il porte le membre simulé. */
    async designateBadge(uid) {
      if (!this.intervention?.simulation) {
        this.error = this.$t('onboarding.field.intervention.simulation.member-first')
        return
      }
      await this.run(async () => {
        this.intervention = await FieldRepository.simulateMember(this.intervention.id, this.intervention.simulation.memberId, uid)
      })
    },

    async run(action) {
      this.working = true
      this.error = null
      try {
        await action()
        this.$emit('change', this.intervention)
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.working = false
      }
    },
  },
}
</script>

<style scoped>
.field-intervention--open {
  border-left: 4px solid var(--v-primary-base);
}

.field-intervention__title {
  font-weight: 500;
  font-size: 1.05rem;
  margin-bottom: 8px;
}

.field-intervention__test {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
}

.field-intervention__test--active {
  background: rgba(76, 175, 80, 0.12);
}

.field-intervention__simulation {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px dashed rgba(76, 175, 80, 0.6);
}
</style>
